import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { UsersService } from './api/users.service';
import { BehaviorSubject, catchError, map, Observable, of, shareReplay, tap } from 'rxjs';
import { UserDetailsInterface } from '../../../core/models/user.model';
import { StorageService } from '../../../core/services/storage.service';
import { ErrorService } from '../../../core/services/error.service';

@Injectable({
    providedIn: 'root',
})
export class AccountService {
    userDetails: BehaviorSubject<UserDetailsInterface | null> = new BehaviorSubject<UserDetailsInterface | null>(null);
    isLoading = false;
    isLoggingOut = false; // FIX: Prevent guards from fetching user details during logout
    private inFlightRequest$: Observable<UserDetailsInterface | null> | null = null; // FIX: Store in-flight request for race condition

    constructor(
        private errorService: ErrorService,
        private router: Router,
        private storageService: StorageService,
        private userService: UsersService,
    ) {}

    public getUserDetails(reload = false): Observable<UserDetailsInterface | null> {
        // FIX: Prevent guards from fetching user details during logout flow
        if (this.isLoggingOut) {
            return of(null);
        }

        if (!this.isAuthenticated()) {
            return of(null);
        }

        if (this.userDetails.value && !reload) {
            return of(this.userDetails.value);
        }

        // FIX: Return the in-flight request if one exists
        // This ensures multiple guards subscribe to the SAME HTTP request
        // instead of getting null from the BehaviorSubject
        if (this.isLoading && this.inFlightRequest$) {
            return this.inFlightRequest$;
        }

        this.isLoading = true;

        this.inFlightRequest$ = this.fetchUserDetails().pipe(
            tap(() => {
                this.isLoading = false;
                this.inFlightRequest$ = null; // Clear after completion
            }),
            shareReplay(1),
        );

        return this.inFlightRequest$;
    }

    public isAuthenticated(): boolean {
        const hasAccessToken = !!this.storageService.accessToken;
        const hasRefreshToken = !!this.storageService.refreshToken;
        return hasAccessToken || hasRefreshToken;
    }

    /**
     * FIX: Reset the logout flag after logout flow completes
     * This ensures that normal authentication flows can proceed after logout
     */
    public completeLogout(): void {
        this.isLoggingOut = false;
        this.userDetails.next(null);
    }

    private fetchUserDetails(): Observable<UserDetailsInterface | null> {
        return this.userService.getUserDetails().pipe(
            map((resp) => {
                const result = resp.result;
                this.userDetails.next(result);

                return result;
            }),
            catchError((error) => {
                const currentUrl = this.router.url;
                const isOnVerifyEmailPage = currentUrl.includes('/verify-email');

                // FIX: Don't show error modal when on verify-email page
                // The verify-email page should work silently even with expired tokens
                if (!isOnVerifyEmailPage) {
                    this.errorService.showError(error);
                }

                return of(null);
            }),
        );
    }
}
